/* eslint-disable no-magic-numbers */
import workerpool, { Pool } from 'workerpool';

import { QuantizedMeshData } from '../types/quantized-mesh-data';

/*
 * This class interfaces with the workerpool library to manage a pool of workers.
 * It allows multiple decoding tasks to be processed in parallel,
 * reducing the time taken to decode large datasets.
 */
export class QuantizedMeshWorkerPool {
  private readonly pool: Pool;

  constructor(
    private readonly workerScriptUrl: string,
    private readonly poolSize = Math.max(
      1,
      Math.floor(navigator.hardwareConcurrency) - 2
    )
  ) {
    this.poolSize = Math.max(1, poolSize); // Ensure at least one worker

    this.pool = workerpool.pool(this.workerScriptUrl, {
      maxWorkers: this.poolSize,
    });
  }

  /**
   * Submits data to be decoded by an available worker.
   * @param {*} dataToDecode The data needed for decoding (e.g., ArrayBuffer of the tile).
   * Ensure this data is structured cloneable or an ArrayBuffer for transfer.
   * @returns {Promise<object>} A promise that resolves with the decoded data object,
   * or rejects with an error.
   */
  public async decode(
    dataToDecode: ArrayBuffer
  ): Promise<QuantizedMeshData | null> {
    try {
      const result = await this.pool.exec<
        (arrayBuffer: ArrayBuffer) => Promise<QuantizedMeshData>
      >('decode', [dataToDecode], { transfer: [dataToDecode] });
      return result;
    } catch (err) {
      console.error('An error occurred during decoding in the worker:', err);
      return null;
    }
  }

  /**
   * Terminates all workers in the pool. Call this when the pool is no longer needed.
   */
  public terminate(): void {
    void this.pool.terminate(true);
  }
}
